/**
 * A client list, read in the browser (notes/AUDIENCE.md 9.1): the spreadsheet's email and phone columns
 * only, emails trimmed and lowercased, phone numbers given their country code, blanks and duplicates
 * dropped, in batches of up to 10,000. Nothing else in the file leaves the browser. Reads .csv, .tsv, .txt
 * and .xlsx (a zip of XML, opened with the platform's own unzip; no DOM, so tests can run it too).
 */

export interface Person {
  email?: string;
  phone?: string;
}

export interface Picked {
  people: Person[];
  /** Phone numbers without a country code, which need one picked before sending. */
  needsCode: number;
  /** Rows with neither a usable email nor phone. */
  skipped: number;
  /** The file had no email or phone column. */
  noColumns: boolean;
}

export const BATCH = 10_000;

/** Country dial codes for numbers written without one, by ISO country. */
export const DIAL_CODES: [string, string, string][] = [
  ["US", "United States", "+1"],
  ["IN", "India", "+91"],
  ["GB", "United Kingdom", "+44"],
  ["CA", "Canada", "+1"],
  ["AU", "Australia", "+61"],
  ["NZ", "New Zealand", "+64"],
  ["IE", "Ireland", "+353"],
  ["AE", "United Arab Emirates", "+971"],
  ["SA", "Saudi Arabia", "+966"],
  ["QA", "Qatar", "+974"],
  ["SG", "Singapore", "+65"],
  ["MY", "Malaysia", "+60"],
  ["ID", "Indonesia", "+62"],
  ["PH", "Philippines", "+63"],
  ["HK", "Hong Kong", "+852"],
  ["JP", "Japan", "+81"],
  ["KR", "South Korea", "+82"],
  ["PK", "Pakistan", "+92"],
  ["BD", "Bangladesh", "+880"],
  ["LK", "Sri Lanka", "+94"],
  ["NP", "Nepal", "+977"],
  ["ZA", "South Africa", "+27"],
  ["NG", "Nigeria", "+234"],
  ["KE", "Kenya", "+254"],
  ["DE", "Germany", "+49"],
  ["FR", "France", "+33"],
  ["ES", "Spain", "+34"],
  ["IT", "Italy", "+39"],
  ["NL", "Netherlands", "+31"],
  ["BR", "Brazil", "+55"],
  ["MX", "Mexico", "+52"],
];

/** The dial code for the browser's region ("en-IN" is +91), or the United States'. */
export function defaultDial(locale: string | undefined): string {
  const region = (locale ?? "").split(/[-_]/)[1]?.toUpperCase();
  return DIAL_CODES.find(([c]) => c === region)?.[2] ?? "+1";
}

// --- Reading the file ------------------------------------------------------------------------------

/** Rows of a delimited text file, with quoted fields; the delimiter is whichever the first line uses most. */
export function parseDelimited(text: string): string[][] {
  const body = text.replace(/^\uFEFF/, "");
  const nl = body.indexOf("\n");
  const first = nl < 0 ? body : body.slice(0, nl);
  const delim = [",", ";", "\t"].map((d) => [d, first.split(d).length] as const).toSorted((a, b) => b[1] - a[1])[0][0];
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let quoted = false;
  for (let i = 0; i < body.length; i++) {
    const ch = body[i];
    if (quoted) {
      if (ch === '"' && body[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"' && cell === "") quoted = true;
    else if (ch === delim) {
      row.push(cell);
      cell = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && body[i + 1] === "\n") i++;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = "";
    } else cell += ch;
  }
  if (cell !== "" || row.length) rows.push([...row, cell]);
  return rows.filter((r) => r.some((c) => c.trim()));
}

const u16 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
const u32 = (b: Uint8Array, o: number) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;

/** The files of a zip, by name (stored or deflated entries; what spreadsheet apps write). */
async function unzip(buf: ArrayBuffer, want: (name: string) => boolean): Promise<Map<string, string>> {
  const b = new Uint8Array(buf);
  let end = -1;
  for (let i = b.length - 22; i >= Math.max(0, b.length - 65_557); i--) if (u32(b, i) === 0x06054b50) { end = i; break; }
  if (end < 0) throw new Error("This file is not a spreadsheet Flashadz can read. Save it as .xlsx or .csv.");
  const count = u16(b, end + 10);
  let at = u32(b, end + 16);
  const out = new Map<string, string>();
  for (let k = 0; k < count; k++) {
    if (u32(b, at) !== 0x02014b50) break;
    const method = u16(b, at + 10);
    const size = u32(b, at + 20);
    const nameLen = u16(b, at + 28);
    const extra = u16(b, at + 30);
    const comment = u16(b, at + 32);
    const local = u32(b, at + 42);
    const name = new TextDecoder().decode(b.subarray(at + 46, at + 46 + nameLen));
    at += 46 + nameLen + extra + comment;
    if (!want(name)) continue;
    const data = local + 30 + u16(b, local + 26) + u16(b, local + 28);
    const raw = b.subarray(data, data + size);
    const bytes = method === 0 ? raw : new Uint8Array(await new Response(new Blob([raw]).stream().pipeThrough(new DecompressionStream("deflate-raw"))).arrayBuffer());
    out.set(name, new TextDecoder().decode(bytes));
  }
  return out;
}

const unescapeXml = (s: string) =>
  s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, e: string) =>
    e[0] === "#" ? String.fromCodePoint(e[1].toLowerCase() === "x" ? parseInt(e.slice(2), 16) : Number(e.slice(1))) : ({ amp: "&", lt: "<", gt: ">", quot: '"', apos: "'" } as Record<string, string>)[e.toLowerCase()],
  );
const texts = (xml: string) => [...xml.matchAll(/<t(?:\s[^>]*)?>([\s\S]*?)<\/t>/g)].map((m) => unescapeXml(m[1])).join("");
const colIndex = (ref: string) => [...ref.replace(/\d+/g, "")].reduce((n, ch) => n * 26 + ch.charCodeAt(0) - 64, 0) - 1;

/** The rows of an .xlsx file's first sheet. */
export async function readXlsx(buf: ArrayBuffer): Promise<string[][]> {
  const files = await unzip(buf, (n) => n === "xl/sharedStrings.xml" || /^xl\/worksheets\/sheet\d+\.xml$/.test(n));
  const strings = [...(files.get("xl/sharedStrings.xml") ?? "").matchAll(/<si>([\s\S]*?)<\/si>/g)].map((m) => texts(m[1]));
  const sheet = [...files.keys()].filter((n) => n.startsWith("xl/worksheets/")).toSorted((a, b) => Number(a.match(/\d+/)![0]) - Number(b.match(/\d+/)![0]))[0];
  if (!sheet) throw new Error("This spreadsheet has no sheet Flashadz can read. Save it as .xlsx or .csv.");
  const rows: string[][] = [];
  for (const r of files.get(sheet)!.matchAll(/<row\b[^>]*>([\s\S]*?)<\/row>/g)) {
    const row: string[] = [];
    for (const c of r[1].matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attrs = c[1];
      const inner = c[2] ?? "";
      const ref = attrs.match(/\br="([A-Z]+\d+)"/)?.[1];
      const type = attrs.match(/\bt="(\w+)"/)?.[1];
      const v = inner.match(/<v>([\s\S]*?)<\/v>/)?.[1];
      const value = type === "s" ? (strings[Number(v)] ?? "") : type === "inlineStr" ? texts(inner) : unescapeXml(v ?? "");
      row[ref ? colIndex(ref) : row.length] = value;
    }
    rows.push(Array.from(row, (x) => x ?? ""));
  }
  return rows.filter((r) => r.some((x) => x.trim()));
}

export async function readSpreadsheet(name: string, buf: ArrayBuffer): Promise<string[][]> {
  if (/\.xlsx$/i.test(name)) return readXlsx(buf);
  if (/\.(csv|tsv|txt)$/i.test(name)) return parseDelimited(new TextDecoder().decode(buf));
  throw new Error("Choose a .csv or .xlsx file. In Excel or Google Sheets, save or download it as one.");
}

// --- Picking the people --------------------------------------------------------------------------

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const cleanEmail = (v: string) => {
  const e = v.trim().toLowerCase();
  return EMAIL.test(e) ? e : null;
};

/** A phone number with its country code (+ and digits), or null. Without one, `dial` is added when given. */
export function cleanPhone(v: string, dial: string | null): string | null | "needs_code" {
  const raw = v.trim();
  if (!raw) return null;
  let d = raw.replace(/[^\d+]/g, "");
  if (d.startsWith("00")) d = `+${d.slice(2)}`;
  if (!d.startsWith("+")) {
    const digits = d.replace(/^0+/, "");
    if (digits.length < 6) return null;
    if (!dial) return "needs_code";
    d = `${dial}${digits}`;
  }
  d = `+${d.slice(1).replace(/\+/g, "")}`;
  return /^\+\d{6,16}$/.test(d) ? d : null;
}

function columns(rows: string[][]): { email: number; phone: number; header: boolean } {
  const head = rows[0] ?? [];
  let email = head.findIndex((h) => /e-?mail/i.test(h));
  let phone = head.findIndex((h) => /phone|mobile|cell|whatsapp|\btel\b|contact number/i.test(h));
  const header = email >= 0 || phone >= 0;
  if (!header) {
    const sample = rows.slice(0, 50);
    const width = Math.max(0, ...sample.map((r) => r.length));
    const share = (i: number, test: (v: string) => boolean) => sample.filter((r) => test(r[i] ?? "")).length / Math.max(1, sample.length);
    for (let i = 0; i < width; i++) {
      if (email < 0 && share(i, (v) => EMAIL.test(v.trim())) > 0.5) email = i;
      else if (phone < 0 && share(i, (v) => v.replace(/\D/g, "").length >= 7 && !/[a-z]/i.test(v)) > 0.5) phone = i;
    }
  }
  return { email, phone, header };
}

/** The people in the rows: email and phone only, cleaned and without duplicates. */
export function pickPeople(rows: string[][], dial: string | null): Picked {
  const { email, phone, header } = columns(rows);
  if (email < 0 && phone < 0) return { people: [], needsCode: 0, skipped: rows.length, noColumns: true };
  const seen = new Set<string>();
  const people: Person[] = [];
  let needsCode = 0;
  let skipped = 0;
  for (const r of header ? rows.slice(1) : rows) {
    const e = email >= 0 ? cleanEmail(r[email] ?? "") : null;
    const p = phone >= 0 ? cleanPhone(r[phone] ?? "", dial) : null;
    if (p === "needs_code") needsCode++;
    const ph = p === "needs_code" ? null : p;
    if (!e && !ph) {
      if (p !== "needs_code") skipped++;
      continue;
    }
    const key = `${e ?? ""}|${ph ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    people.push({ ...(e ? { email: e } : {}), ...(ph ? { phone: ph } : {}) });
  }
  return { people, needsCode, skipped, noColumns: false };
}
